import React from 'react';
import { User, Phone, Tag, QrCode, Copy, ExternalLink } from 'lucide-react';
import { PaymentProviderLogo, getProviderFromMethod } from '../PaymentProviderLogo';
import SafeImage from '../SafeImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pure presentational card for a single payment method.
 *
 * Extracted from EventPage.renderPaymentMethod. No state, no API calls.
 * `toast` notifications for individual field copies are emitted via the
 * imported sonner toast API (same behavior as before, just colocated here
 * so the parent doesn't need to inject it).
 *
 * Props:
 *   link         — payment link object
 *   onCopyAll    — () => void, invoked when "Copy All Payment Details" clicked
 *   onCopyField  — (text, label) => void, fires for individual field copies
 */
export default function PaymentMethodCard({ link, onCopyAll, onCopyField }) {
  const isDirect = link.payment_method_type === 'direct';
  const provider = link.payment_provider || getProviderFromMethod(link.method);

  if (isDirect) {
    return (
      <div className="w-full border-2 border-[#1A1A1A] rounded-sm overflow-hidden" data-testid={`payment-link-${link.method}`}>
        {/* Header with logo */}
        <div className="bg-[#111] px-5 py-3 flex items-center gap-3 border-b border-[#1A1A1A]">
          <PaymentProviderLogo provider={provider} method={link.method} size="md" />
          <span className="font-semibold text-[#EAE7E1] text-base">Pay via {link.method}</span>
          <span className="ml-auto text-xs px-2 py-0.5 rounded bg-[#1a2a1a] text-[#6EE7B7] font-medium">Direct Transfer</span>
        </div>
        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          {link.recipient_name && (
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0"><User className="w-3.5 h-3.5 text-[#AFAFAF]" /></div>
              <div className="flex-1"><div className="text-xs text-[#777] uppercase tracking-wider">Send to</div><div className="text-sm font-semibold text-[#EAE7E1]">{link.recipient_name}</div></div>
            </div>
          )}
          {link.email_or_phone && (
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0"><Phone className="w-3.5 h-3.5 text-[#AFAFAF]" /></div>
              <div className="flex-1">
                <div className="text-xs text-[#777] uppercase tracking-wider">Email / Phone</div>
                <div className="text-sm font-semibold text-[#EAE7E1] font-mono">{link.email_or_phone}</div>
              </div>
              <button type="button" onClick={() => onCopyField(link.email_or_phone, 'Contact')} className="text-[#777] hover:text-[#D4AF37] p-1.5 rounded-sm hover:bg-[#1A1A1A] transition-colors" data-testid={`copy-contact-${link.method}`}><Copy className="w-3.5 h-3.5" /></button>
            </div>
          )}
          {link.suggested_memo && (
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0"><Tag className="w-3.5 h-3.5 text-[#AFAFAF]" /></div>
              <div className="flex-1">
                <div className="text-xs text-[#777] uppercase tracking-wider">Include this memo</div>
                <div className="text-sm font-semibold text-[#D4AF37] italic">"{link.suggested_memo}"</div>
              </div>
              <button type="button" onClick={() => onCopyField(link.suggested_memo, 'Memo')} className="text-[#777] hover:text-[#D4AF37] p-1.5 rounded-sm hover:bg-[#1A1A1A] transition-colors" data-testid={`copy-memo-${link.method}`}><Copy className="w-3.5 h-3.5" /></button>
            </div>
          )}
          {link.qr_code_image && (
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0"><QrCode className="w-3.5 h-3.5 text-[#AFAFAF]" /></div>
              <div>
                <div className="text-xs text-[#777] uppercase tracking-wider mb-1">Scan to Pay</div>
                <SafeImage src={`${BACKEND_URL}${link.qr_code_image}`} alt={`${link.method} QR Code`} className="h-28 w-28 rounded" imgClassName="bg-white p-1 object-contain border border-[#1A1A1A]" fallbackLabel="QR" data-testid={`qr-image-${link.method}`} />
              </div>
            </div>
          )}
          {link.instructions && (
            <div className="text-sm text-[#AFAFAF] italic border-t border-[#1A1A1A] pt-3 mt-1">{link.instructions}</div>
          )}
          <button
            type="button"
            onClick={onCopyAll}
            className="w-full mt-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] text-sm font-semibold hover:bg-[#D4AF37]/15 active:scale-[0.98] transition-all"
            data-testid={`copy-all-${link.method}`}
          >
            <Copy className="w-4 h-4" />
            Copy All Payment Details
          </button>
        </div>
      </div>
    );
  }
  // Link-based with provider logo
  return (
    <a href={link.url} target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-3 border-2 border-[#1A1A1A] p-4 hover:border-[#D4AF37] transition-colors font-semibold rounded-sm" data-testid={`payment-link-${link.method}`}>
      <PaymentProviderLogo provider={provider} method={link.method} size="md" />
      <div className="flex-1 min-w-0">
        <div className="text-[#EAE7E1]">{link.method}</div>
        {link.handle && <div className="text-xs text-[#777] font-normal">{link.handle}</div>}
        {link.instructions && <div className="text-sm text-[#AFAFAF] font-normal mt-1">{link.instructions}</div>}
      </div>
      <ExternalLink className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
    </a>
  );
}
